<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Header -->
    <div class="bg-white shadow">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <i class="fas fa-upload text-orange-600 text-3xl mr-3"></i>
            <div>
              <h1 class="text-2xl font-bold text-gray-900">Synchronisation MySQL → MSSQL</h1>
              <p class="text-sm text-gray-500 mt-1">Visites, contrôles et commentaires</p>
            </div>
          </div>
          <div class="flex gap-3">
            <button
              @click="testConnection"
              :disabled="testing || syncing"
              class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i :class="testing ? 'fas fa-spinner fa-spin' : 'fas fa-check-circle'" class="mr-2"></i>
              {{ testing ? 'Test en cours...' : 'Tester connexions' }}
            </button>
            <button
              @click="syncAll"
              :disabled="syncing || loading"
              class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i :class="syncing ? 'fas fa-spinner fa-spin' : 'fas fa-sync-alt'" class="mr-2"></i>
              <span v-if="syncing">Synchronisation...</span>
              <span v-else>Tout synchroniser</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Warning Banner -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
      <div class="bg-amber-50 border border-amber-300 rounded-lg p-4">
        <div class="flex">
          <i class="fas fa-exclamation-triangle text-amber-500 text-xl flex-shrink-0 mt-0.5"></i>
          <div class="ml-3">
            <h3 class="text-sm font-semibold text-amber-800">Opération avancée — direction inverse</h3>
            <div class="mt-1 text-sm text-amber-700 space-y-1">
              <p>
                Cette interface synchronise les données de <strong>MySQL vers MSSQL</strong>.
                MSSQL est la base de données de référence (source habituelle).
              </p>
              <p>
                Utilisez cette page uniquement si vous avez besoin de remonter des visites
                créées dans MySQL (via la PWA) vers MSSQL. Assurez-vous que l'opération
                a été validée avec l'équipe avant de lancer une synchronisation.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <!-- Loading State -->
      <div v-if="loading" class="text-center py-12">
        <i class="fas fa-spinner fa-spin text-orange-600 text-5xl"></i>
        <p class="mt-4 text-gray-600">Chargement des statistiques...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
        <div class="flex">
          <i class="fas fa-exclamation-circle text-red-400 text-xl"></i>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-red-800">Erreur</h3>
            <p class="mt-1 text-sm text-red-700">{{ error }}</p>
          </div>
        </div>
      </div>

      <!-- Tables Grid -->
      <div v-else class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div
          v-for="table in tables"
          :key="table.name"
          class="bg-white rounded-lg shadow hover:shadow-lg transition-shadow duration-200"
        >
          <div class="p-6">
            <!-- Table Header -->
            <div class="flex items-center justify-between mb-4">
              <h3 class="text-lg font-semibold text-gray-900">{{ table.name }}</h3>
              <span
                :class="{
                  'bg-green-100 text-green-800': table.status === 'synced',
                  'bg-yellow-100 text-yellow-800': table.status === 'outdated',
                  'bg-blue-100 text-blue-800': table.status === 'loading',
                  'bg-red-100 text-red-800': table.status === 'error',
                }"
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
              >
                <i v-if="table.status === 'synced'" class="fas fa-check-circle mr-1"></i>
                <i v-else-if="table.status === 'outdated'" class="fas fa-exclamation-triangle mr-1"></i>
                <i v-else-if="table.status === 'loading'" class="fas fa-spinner fa-spin mr-1"></i>
                <i v-else class="fas fa-times-circle mr-1"></i>
                {{ statusLabel(table.status) }}
              </span>
            </div>

            <!-- Error Message -->
            <div v-if="table.status === 'error' && table.error" class="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p class="text-xs text-red-700 break-words">{{ table.error }}</p>
            </div>

            <!-- Statistics — direction MySQL → MSSQL -->
            <div class="space-y-2 mb-4">
              <div class="flex justify-between text-sm">
                <span class="text-gray-600">MySQL <span class="text-xs text-green-600">(source)</span> :</span>
                <span class="font-semibold text-gray-900">
                  {{ table.mysqlCount !== undefined && table.mysqlCount !== '...' ? formatNumber(table.mysqlCount) : (table.mysqlCount || 'N/A') }}
                </span>
              </div>
              <div class="flex justify-between text-sm">
                <span class="text-gray-600">MSSQL <span class="text-xs text-orange-600">(cible)</span> :</span>
                <span class="font-semibold text-gray-900">
                  {{ table.mssqlCount !== undefined && table.mssqlCount !== '...' ? formatNumber(table.mssqlCount) : (table.mssqlCount || 'N/A') }}
                </span>
              </div>
              <div v-if="table.difference !== undefined && table.difference !== 0" class="flex justify-between text-sm">
                <span class="text-gray-600">À synchroniser :</span>
                <span class="font-semibold" :class="table.difference > 0 ? 'text-orange-600' : 'text-blue-600'">
                  {{ table.difference > 0 ? '+' : '' }}{{ formatNumber(table.difference) }}
                </span>
              </div>
            </div>

            <!-- Per-table sync button -->
            <button
              @click="syncSingleTable(table.name)"
              :disabled="syncing"
              class="w-full inline-flex justify-center items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i
                :class="currentSyncingTable === table.name ? 'fas fa-spinner fa-spin' : 'fas fa-sync-alt'"
                class="mr-1.5"
              ></i>
              {{ currentSyncingTable === table.name ? 'Synchronisation...' : 'Synchroniser' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Logs Panel -->
      <div v-if="logs.length > 0" class="mt-8 bg-white rounded-lg shadow">
        <div class="px-6 py-4 border-b border-gray-200">
          <div class="flex items-center justify-between">
            <h2 class="text-lg font-semibold text-gray-900">Logs de synchronisation</h2>
            <button @click="clearLogs" class="text-sm text-gray-600 hover:text-gray-900">
              Effacer
            </button>
          </div>
        </div>
        <div class="px-6 py-4 max-h-96 overflow-y-auto">
          <div v-for="(log, index) in logs" :key="index" class="flex items-start py-2 text-sm">
            <span class="text-gray-500 mr-3 flex-shrink-0">{{ log.time }}</span>
            <i
              v-if="log.type === 'success'"
              class="fas fa-check-circle text-green-500 mr-2 flex-shrink-0 mt-0.5"
            ></i>
            <i
              v-else-if="log.type === 'error'"
              class="fas fa-times-circle text-red-500 mr-2 flex-shrink-0 mt-0.5"
            ></i>
            <i
              v-else
              class="fas fa-info-circle text-blue-500 mr-2 flex-shrink-0 mt-0.5"
            ></i>
            <span :class="log.type === 'error' ? 'text-red-700' : 'text-gray-700'">{{ log.message }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'

export default {
  name: 'VisitSyncToMssql',

  data() {
    return {
      tables: [],
      loading: false,
      syncing: false,
      testing: false,
      currentSyncingTable: null,
      error: null,
      logs: [],
    }
  },

  mounted() {
    this.loadStats()
  },

  methods: {
    async loadStats() {
      this.loading = true
      this.error = null

      try {
        const stats = await api.getVisitSyncToMssqlStats()

        if (!stats || stats.length === 0) {
          this.error = 'Aucune table configurée pour la synchronisation MySQL → MSSQL'
          this.loading = false
          return
        }

        this.tables = stats
        this.addLog('info', `Statistiques chargées pour ${stats.length} tables`)
      } catch (err) {
        this.error = err.message || 'Erreur lors du chargement des statistiques'
        this.addLog('error', this.error)
      } finally {
        this.loading = false
      }
    },

    async testConnection() {
      this.testing = true
      this.addLog('info', 'Test des connexions aux bases de données (MySQL + MSSQL)...')

      try {
        const result = await api.testVisitSyncToMssqlConnection()

        if (result.success) {
          const mssql = result.connections?.mssql
          const mysql = result.connections?.mysql
          this.addLog(
            'success',
            `✓ Connexions OK — MySQL: ${mysql?.responseTime ?? '?'}ms, MSSQL: ${mssql?.responseTime ?? '?'}ms`
          )
          await this.loadStats()
        } else {
          this.addLog('error', `✗ Test échoué: ${result.message || 'Erreur inconnue'}`)
        }
      } catch (err) {
        this.addLog('error', `✗ Erreur de connexion: ${err.message}`)
      } finally {
        this.testing = false
      }
    },

    async syncAll() {
      if (!confirm('Voulez-vous vraiment synchroniser toutes les visites, contrôles et commentaires de MySQL vers MSSQL ?')) {
        return
      }

      this.syncing = true
      this.addLog('info', '🚀 Démarrage de la synchronisation MySQL → MSSQL...')

      try {
        const result = await api.syncVisitsToMssql(false)

        if (result.success) {
          this.addLog('success', `🎉 ${result.message}`)

          // Log per-table results
          if (result.results && result.results.length > 0) {
            result.results.forEach((tableResult) => {
              const type = tableResult.success ? 'success' : 'error'
              this.addLog(type, tableResult.message || 'OK')
            })
          }

          await this.loadStats()
        } else {
          this.addLog('error', `⚠️ ${result.message || 'Synchronisation terminée avec des erreurs'}`)

          if (result.results) {
            result.results.forEach((tableResult) => {
              if (!tableResult.success) {
                this.addLog('error', tableResult.message || 'Erreur inconnue')
                if (tableResult.errorDetails?.length) {
                  tableResult.errorDetails.slice(0, 3).forEach((detail) => {
                    this.addLog('error', `   ID ${detail.recordId ?? '?'}: ${detail.error}`)
                  })
                }
              }
            })
          }
        }
      } catch (err) {
        this.addLog('error', `Erreur lors de la synchronisation globale: ${err.message}`)
      } finally {
        this.syncing = false
      }
    },

    async syncSingleTable(tableName) {
      if (!confirm(`Voulez-vous vraiment synchroniser la table "${tableName}" de MySQL vers MSSQL ?`)) {
        return
      }

      this.syncing = true
      this.currentSyncingTable = tableName
      this.addLog('info', `Synchronisation de "${tableName}" MySQL → MSSQL...`)

      try {
        const result = await api.syncVisitTableToMssql(tableName, false)

        const type = result.success ? 'success' : 'error'
        this.addLog(type, result.message || (result.success ? 'OK' : 'Erreur'))

        if (!result.success && result.errorDetails?.length) {
          result.errorDetails.slice(0, 5).forEach((detail) => {
            this.addLog('error', `   ID ${detail.recordId ?? '?'}: ${detail.error}`)
          })
        }

        await this.loadStats()
      } catch (err) {
        this.addLog('error', `Erreur lors de la synchronisation de "${tableName}": ${err.message}`)
      } finally {
        this.syncing = false
        this.currentSyncingTable = null
      }
    },

    addLog(type, message) {
      const now = new Date()
      this.logs.unshift({
        type,
        message,
        time: now.toLocaleTimeString('fr-FR'),
        timestamp: now.getTime(),
      })

      if (this.logs.length > 50) {
        this.logs = this.logs.slice(0, 50)
      }
    },

    clearLogs() {
      this.logs = []
    },

    statusLabel(status) {
      const labels = {
        synced:   'À jour',
        outdated: 'À synchroniser',
        error:    'Erreur',
        loading:  'Chargement...',
      }
      return labels[status] ?? status
    },

    formatNumber(value) {
      return typeof value === 'number' ? value.toLocaleString('fr-FR') : value
    },
  },
}
</script>

<style scoped>
/* No additional styles needed — Tailwind CSS handles everything */
</style>
